import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import { getImageURL } from '../../utils/api';

function fmtDateTime(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function PersonAvatar({ name, imagePath, size = 'md' }) {
  const [imgError, setImgError] = useState(false);
  const dim = size === 'lg' ? 'h-16 w-16' : 'h-9 w-9';
  const placeholderSize = size === 'lg' ? '192x192' : '96x96';
  const hasImage = imagePath && !imgError;
  // Same fallback convention as Timetable.jsx: placehold.co when there's no valid photo.
  const src = hasImage
    ? getImageURL(imagePath)
    : `https://placehold.co/${placeholderSize}?text=${encodeURIComponent(name || 'Staff')}`;
  return (
    <img
      src={src}
      alt={name}
      onError={() => setImgError(true)}
      className={`${dim} shrink-0 rounded-full border-2 border-white bg-gray-100 object-cover shadow ring-1 ring-gray-200`}
    />
  );
}

function RatingStars({ value, size = 'sm' }) {
  if (value === null || value === undefined) return <span className="text-xs italic text-gray-400">—</span>;
  const dim = size === 'lg' ? 'h-5 w-5' : 'h-3.5 w-3.5';
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <svg key={i} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" className={`${dim} ${i <= value ? 'text-amber-400' : 'text-gray-200'}`} fill="currentColor">
          <path d="M10 1.5l2.6 5.27 5.82.85-4.21 4.1.99 5.8L10 14.9l-5.2 2.62.99-5.8-4.21-4.1 5.82-.85L10 1.5z" />
        </svg>
      ))}
    </span>
  );
}

export default function LecturerRatingsDetailView() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const staffId = searchParams.get('staffId');
  const staffNameFallback = searchParams.get('staffName') || '';
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');

  useEffect(() => {
    let isMounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await axiosInstance.get('/admin/dashboard/lecturer-ratings/detail', {
          params: { staffId, startDate, endDate },
        });
        if (!isMounted) return;
        setData(resp?.data || null);
      } catch (err) {
        if (isMounted) setError(err?.response?.data?.message || 'Failed to load rating detail');
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const staff = data?.staff;
  const ratings = data?.ratings || [];

  return (
    <DashboardLayout title="Lecturer Rating — Detail">
      <div className="mx-auto max-w-3xl">
        <div className="mb-4 flex items-center justify-between">
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : window.close())}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
        </div>

        {/* Header */}
        <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 p-5">
          <div className="flex items-center gap-4">
            <PersonAvatar name={staff?.staffName || staffNameFallback} imagePath={staff?.imagePath} size="lg" />
            <div className="min-w-0 flex-1">
              <h2 className="truncate text-lg font-bold text-gray-900">{staff?.staffName || staffNameFallback || `Staff #${staffId}`}</h2>
              <p className="text-xs text-gray-600">{startDate} to {endDate}</p>
            </div>
            {!loading && !error && (
              <div className="shrink-0 text-right">
                <RatingStars value={data?.avgRating ? Math.round(data.avgRating) : null} size="lg" />
                <p className="mt-1 text-sm font-bold text-amber-700">
                  {data?.avgRating !== null && data?.avgRating !== undefined ? `${data.avgRating.toFixed(2)} / 5` : 'No ratings'}
                </p>
                <p className="text-[11px] text-gray-500">{data?.ratingsCount || 0} rating{data?.ratingsCount === 1 ? '' : 's'}</p>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <p className="rounded-xl border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
            Loading ratings and comments…
          </p>
        ) : error ? (
          <p className="rounded-xl border border-red-200 bg-red-50 p-8 text-center text-sm text-red-600">{error}</p>
        ) : ratings.length === 0 ? (
          <p className="rounded-xl border border-gray-200 bg-white p-10 text-center text-sm italic text-gray-400">
            No ratings found for this lecturer in the selected date range.
          </p>
        ) : (
          <div className="space-y-3">
            {ratings.map((r) => (
              <div key={r.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2.5">
                    <PersonAvatar name={r.studentName || r.studentId} imagePath={r.studentImagePath} />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-800">{r.studentName || r.studentId || 'Anonymous student'}</p>
                      <p className="text-[11px] text-gray-400">
                        {r.subjectName && <span>{r.subjectName}</span>}
                        {r.subjectName && r.batchName && <span> · </span>}
                        {r.batchName && <span>{r.batchName}</span>}
                      </p>
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <RatingStars value={r.rating} />
                    <p className="mt-0.5 text-[10px] text-gray-400">{fmtDateTime(r.createdAt)}</p>
                  </div>
                </div>
                {r.reason && (
                  <p className="rounded-lg bg-gray-50 px-3 py-2 text-xs leading-relaxed text-gray-700">{r.reason}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
